import React from 'react';
import PropTypes from 'prop-types';
import { PlayArrow, DeleteOutline } from '@mui/icons-material';
import { Box, Button, Typography, CircularProgress, Chip } from '@mui/material';
import PgHomeSectionPaper from './PgHomeSectionPaper';
import { formatRelativeTime } from '../../../globals/utils/homeHelpers';

/** Shared draft bond list for PG and Law Firm home dashboards. */
export default function HomeDraftsListSection({
  copy,
  drafts,
  draftCount,
  loadingDrafts,
  deletingId,
  resolveResumePath,
  onResume,
  onDelete,
  paperSx,
  maxItems = 5,
  formatRelativeTimeFn,
}) {
  return (
    <PgHomeSectionPaper
      sx={{
        flex: '0.85 1 auto',
        display: 'flex',
        flexDirection: 'column',
        ...paperSx,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.25, color: '#333333' }}>
              {copy.title}
            </Typography>
            <Chip size="small" color="primary" label={draftCount} />
          </Box>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
            {copy.subtitle}
          </Typography>
        </Box>
        {loadingDrafts && <CircularProgress size={18} />}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
        {drafts.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
            {copy.emptyDrafts}
          </Typography>
        ) : (
          drafts.slice(0, maxItems).map((row) => {
            const resumePath = resolveResumePath(row);
            return (
              <Box
                key={row?.bond_cache_id}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: (theme) => (theme.palette.mode === 'light' ? '#ffffff' : 'background.default'),
                  border: '1px solid rgba(37, 99, 235, 0.12)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', noWrap: true, mb: 1 }}>
                    {row?.name || 'Untitled'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {row?.bond_type && `${row.bond_type}`}
                    {row?.bond_type && row?.created_at && ' • '}
                    {row?.created_at &&
                      (formatRelativeTimeFn ?? formatRelativeTime)(row.created_at)}
                  </Typography>
                </Box>
                <Box display="flex" gap={0.5}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlayArrow sx={{ fontSize: 14 }} />}
                    onClick={() => resumePath && onResume(resumePath)}
                    disabled={!resumePath}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.7rem',
                      px: 1,
                      py: 0.5,
                      minWidth: 'auto',
                      color: '#2563eb',
                      borderColor: 'rgba(37, 99, 235, 0.3)',
                    }}
                  >
                    {copy.resume}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      deletingId === row?.bond_cache_id ? (
                        <CircularProgress size={14} />
                      ) : (
                        <DeleteOutline sx={{ fontSize: 14 }} />
                      )
                    }
                    onClick={() => onDelete(row?.bond_cache_id)}
                    disabled={deletingId === row?.bond_cache_id}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.7rem',
                      px: 1,
                      py: 0.5,
                      minWidth: 'auto',
                      color: '#dc2626',
                      borderColor: 'rgba(220, 38, 38, 0.3)',
                    }}
                  >
                    {copy.delete}
                  </Button>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </PgHomeSectionPaper>
  );
}

HomeDraftsListSection.propTypes = {
  copy: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    emptyDrafts: PropTypes.string.isRequired,
    resume: PropTypes.string.isRequired,
    delete: PropTypes.string.isRequired,
  }).isRequired,
  drafts: PropTypes.array.isRequired,
  draftCount: PropTypes.number.isRequired,
  loadingDrafts: PropTypes.bool.isRequired,
  deletingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resolveResumePath: PropTypes.func.isRequired,
  onResume: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  paperSx: PropTypes.object,
  maxItems: PropTypes.number,
  formatRelativeTimeFn: PropTypes.func,
};

HomeDraftsListSection.defaultProps = {
  deletingId: null,
  paperSx: {},
  maxItems: 5,
  formatRelativeTimeFn: null,
};
